import { useCallback, useEffect, useState } from 'react';
import { api, type AdminReport } from '../api';
import { Loading } from '../components';
import { formatRelative } from '../format';
import { Link } from '../router';
import { useApp } from '../store';

export function AdminView() {
  const { account, authStatus, toast } = useApp();
  const [reports, setReports] = useState<AdminReport[] | null>(null);

  const load = useCallback(() => {
    api.adminReports()
      .then((data) => setReports(data.reports))
      .catch(() => setReports([]));
  }, []);

  useEffect(() => {
    if (account?.isAdmin) load();
  }, [account, load]);

  if (authStatus === 'loading') return <Loading />;
  if (!account?.isAdmin) {
    return (
      <div className="page narrow">
        <h1>Admin only</h1>
        <p>This area is for TeamCraft moderators.</p>
        <Link to="/" className="secondary small">Go home</Link>
      </div>
    );
  }

  async function resolve(id: string, status: 'resolved' | 'dismissed') {
    try {
      await api.resolveReport(id, status);
      toast(`Report ${status}.`, 'success');
      load();
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not update the report.', 'error');
    }
  }

  return (
    <div className="page narrow">
      <div className="section-heading">
        <div>
          <span className="eyebrow">Moderation</span>
          <h1>Reports</h1>
        </div>
      </div>
      {reports === null ? (
        <Loading label="Loading reports…" />
      ) : reports.length === 0 ? (
        <p className="muted">No reports. Quiet is good.</p>
      ) : (
        <div className="security-rows">
          {reports.map((report) => (
            <div key={report.id} className={`report-row ${report.status !== 'open' ? 'closed' : ''}`}>
              <div className="report-head">
                <strong>{report.reason}</strong>
                <span className="pill">{report.status}</span>
                <span className="mono muted">{formatRelative(report.created_at)}</span>
              </div>
              <p>
                <Link to={`/players/${report.subject_username}`} className="inline-link">{report.subject_username}</Link>
                {' '}reported by {report.reporter_username}
                {report.session_id && <> · <Link to={`/sessions/${report.session_id}`} className="inline-link">session</Link></>}
              </p>
              {report.details && <p className="report-details">{report.details}</p>}
              {report.status === 'open' && (
                <div className="action-bar">
                  <button className="secondary small" onClick={() => resolve(report.id, 'resolved')}>Resolve</button>
                  <button className="ghost small" onClick={() => resolve(report.id, 'dismissed')}>Dismiss</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
